import { gql } from "@apollo/client";
import { Flex, Skeleton } from "@chakra-ui/react";
import _ from "lodash";
import React, {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { GenericError } from "src/components/Feedback/GenericError";
import { RemoteDataView } from "src/components/Layout/RemoteDataView";
import { diffFormSchema } from "src/generated/hooks/form-service/requests/services.gen";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import {
  findQuestionById,
  findSectionById,
} from "src/services/formTemplate/v2";
import * as AF from "src/types/formTemplate";
import {
  FormTemplate,
  FormTemplateSimpleDiff,
} from "src/types/formTemplate/v2";
import * as GQL from "src/types/graphql";
import * as RD from "src/types/remoteData";
import { useFormTemplateContext } from "../../formTemplateContext";
import * as QV from "./components/QuestionVisibility";
import * as Contents from "./contents";
import client from "./FormTemplateClient";
import { Action, useFormTemplateReducer } from "./reducer";
import { getFullQuestions, State } from "./types/state";

type ContextValue = {
  state: State;
  dispatch: (action: Action) => void;
  formTemplateId: uuid;
  enrollmentPeriodId: uuid;
  invalidQuestionVisibility: readonly uuid[];
  grades: Grade[];
  hasUnsavedChanges: boolean;
  hasUnpublishedChanges: boolean;
  saveFormSchema: () => Promise<void>;
  isPublishedMode: boolean;
  hasDirtyForm: boolean;
  setFormDirty: (dirty: boolean) => void;
  helpers: Helpers;
} & PublishedFormTemplateContextValue;

type Helpers = {
  isUnpublishedQuestion: (questionId: string) => boolean;
  isUnpublishedSection: (sectionId: string) => boolean;
  isUnpublishedAnswerOption: (questionId: string, optionId: string) => boolean;
  isUnsavedQuestion: (questionId: string) => boolean;
  isUnsavedSection: (sectionId: string) => boolean;
  isNewSection: (sectionId: string) => boolean;
};

type PublishedFormTemplateContextValue =
  | {
      isPublishedMode: true;
      publishedFormTemplate: FormTemplate;
    }
  | {
      isPublishedMode: false;
    };

type Grade = {
  id: string;
  value: string | null;
  label: string;
};

const EditFormTemplateContext = createContext<ContextValue | undefined>(
  undefined
);

type Props = {
  children: React.ReactNode;
  enrollmentPeriodId: string;
  formTemplateId: string;
};

export const EditFormTemplateProvider: FunctionComponent<Props> = ({
  children,
  formTemplateId,
  enrollmentPeriodId,
}) => {
  const { mode, formTemplate } = useFormTemplateContext();
  const version = React.useMemo(() => {
    if (
      formTemplate.mode === GQL.form_template_mode_enum.Published &&
      mode === "view"
    ) {
      return "Published";
    }

    return "Latest";
  }, [formTemplate.mode, mode]);

  const formTemplateRD = client.useGetFormTemplate(formTemplateId, version);
  const publishedFormTemplateRD = client.useGetFormTemplate(
    formTemplateId,
    "Published"
  );

  const avelaToast = useAvelaToast();

  // Note: I decided not to use tanstack query for fetching diff so we don't have to deal with managing caching - abadi
  const [diffRD, setDiffRD] = React.useState<
    RD.RemoteData<Error, AF.V2.FormTemplateSimpleDiff | undefined>
  >(RD.loading());
  React.useEffect(() => {
    diffFormSchema({
      path: {
        formTemplateId: formTemplateId,
      },
    })
      .then((response) => {
        setDiffRD(RD.success(response.data?.data));
      })
      .catch((error) => {
        if (!avelaToast.isActive("diffError")) {
          avelaToast.error({
            toastId: "diffError",
            title: "Error detecting unpublished changes",
          });
        }
        console.error(error);
        setDiffRD(RD.success(undefined));
      });
  }, [formTemplateId, avelaToast]);

  const { remoteData: gradesRD } = useRemoteDataQuery<
    GQL.GetGradesForEnrollmentPeriodDraftMode,
    GQL.GetGradesForEnrollmentPeriodDraftModeVariables
  >(
    gql`
      query GetGradesForEnrollmentPeriodDraftMode($enrollmentPeriodId: uuid!) {
        grade_config(
          where: {
            grades: { enrollment_period_id: { _eq: $enrollmentPeriodId } }
          }
          order_by: [{ order: asc }]
        ) {
          id
          label
          value
        }
      }
    `,
    {
      variables: { enrollmentPeriodId: enrollmentPeriodId },
      fetchPolicy: "cache-and-network",
    }
  );

  return (
    <RemoteDataView
      remoteData={RD.toTuple4(
        formTemplateRD,
        gradesRD,
        publishedFormTemplateRD,
        diffRD
      )}
      error={(error) => <GenericError error={error} />}
      loading={<Loading />}
      reloading={null}
    >
      {([formTemplate, { grade_config }, publishedFormTemplate, diff]) => {
        return (
          <EditFormTemplateProviderInternal
            formTemplate={formTemplate}
            publishedFormTemplate={publishedFormTemplate}
            enrollmentPeriodId={enrollmentPeriodId}
            grades={grade_config}
            diff={diff}
          >
            {children}
          </EditFormTemplateProviderInternal>
        );
      }}
    </RemoteDataView>
  );
};

type InternalProps = {
  formTemplate: FormTemplate;
  publishedFormTemplate: FormTemplate;
  enrollmentPeriodId: string;
  grades: GQL.GetGradesForEnrollmentPeriodDraftMode_grade_config[];
  diff?: FormTemplateSimpleDiff;
  children: React.ReactNode;
};
const EditFormTemplateProviderInternal = ({
  formTemplate,
  publishedFormTemplate,
  enrollmentPeriodId,
  grades,
  diff,
  children,
}: InternalProps) => {
  const { formTemplate: formTemplateGQL } = useFormTemplateContext();
  const formTemplateState = useFormTemplateReducer(formTemplate, diff);

  const updateFormSchema = client.useUpdateFormSchema();
  const { state, dispatch } = formTemplateState;

  const fullQuestions = useMemo(
    () => getFullQuestions(state).toArray(),
    [state]
  );
  const invalidQuestionVisibility = useMemo(
    () => QV.findInvalidQuestionVisibility(fullQuestions),
    [fullQuestions]
  );

  const [hasDirtyForm, setDirtyForm] = React.useState(false);

  // Computed values for change detection
  const hasUnsavedChanges = useMemo(
    () => Contents.hasChanges(state.get("original"), state.get("draft")),
    [state]
  );

  const isPublishedMode =
    formTemplateGQL.mode === GQL.form_template_mode_enum.Published;

  const hasUnpublishedChanges = useMemo((): boolean => {
    if (!isPublishedMode) {
      // form template is not published yet, so yeah it has unpublished changes
      return true;
    }

    const diff = state.get("diff");
    return !(
      _.isEmpty(diff.questions) &&
      _.isEmpty(diff.sections) &&
      _.isEmpty(diff.verifications)
    );
  }, [isPublishedMode, state]);

  // Reusable save function
  const saveFormSchema = useCallback(async () => {
    const draft = state.get("draft");
    await updateFormSchema(formTemplate.id, draft);
    dispatch({ type: "SetFormTemplate", formTemplate: draft });
  }, [state, updateFormSchema, formTemplate.id, dispatch]);

  const publishedFormTemplateContextValue: PublishedFormTemplateContextValue =
    isPublishedMode
      ? {
          isPublishedMode,
          publishedFormTemplate: publishedFormTemplate,
        }
      : { isPublishedMode };

  const isUnpublishedQuestion = React.useCallback(
    (questionId: string): boolean => {
      if (!isPublishedMode) {
        return true;
      }

      const originalQuestion = findQuestionById(
        publishedFormTemplate,
        questionId
      );
      return originalQuestion === undefined;
    },
    [isPublishedMode, publishedFormTemplate]
  );

  const isUnsavedQuestion = React.useCallback(
    (questionId: string): boolean => {
      const original = formTemplateState.state.get("original");
      const draft = formTemplateState.state.get("draft");
      return Contents.Question.isUnsaved(questionId, original, draft);
    },
    [formTemplateState.state]
  );

  const isUnpublishedSection = React.useCallback(
    (sectionId: string): boolean => {
      if (!isPublishedMode) {
        return true;
      }

      const [originalSection] = findSectionById(
        publishedFormTemplate,
        sectionId
      );
      return originalSection === undefined;
    },
    [isPublishedMode, publishedFormTemplate]
  );

  const isUnsavedSection = React.useCallback(
    (sectionId: string): boolean => {
      const original = formTemplateState.state.get("original");
      const draft = formTemplateState.state.get("draft");
      return Contents.Section.isUnsaved(sectionId, original, draft);
    },
    [formTemplateState.state]
  );

  const isNewSection = React.useCallback(
    (sectionId: string): boolean => {
      const original = formTemplateState.state.get("original");
      const draft = formTemplateState.state.get("draft");
      return Contents.Section.isNew(sectionId, original, draft);
    },
    [formTemplateState.state]
  );

  const isUnpublishedAnswerOption = React.useCallback(
    (questionId: string, optionId: string) => {
      if (!isPublishedMode) {
        return true;
      }
      const originalQuestion = findQuestionById(
        publishedFormTemplate,
        questionId
      );
      if (!originalQuestion || !("options" in originalQuestion)) {
        return true;
      }
      const originalOption = originalQuestion.options.find(
        (option) => option.id === optionId
      );
      return originalOption === undefined;
    },
    [isPublishedMode, publishedFormTemplate]
  );

  const helpers: Helpers = {
    isUnpublishedQuestion,
    isUnpublishedSection,
    isUnpublishedAnswerOption,
    isUnsavedQuestion,
    isUnsavedSection,
    isNewSection,
  };

  return (
    <EditFormTemplateContext.Provider
      value={{
        ...formTemplateState,
        ...publishedFormTemplateContextValue,
        helpers,
        formTemplateId: formTemplate.id,
        enrollmentPeriodId: enrollmentPeriodId,
        invalidQuestionVisibility,
        grades,
        saveFormSchema,
        hasUnsavedChanges,
        hasUnpublishedChanges,
        hasDirtyForm: hasDirtyForm,
        setFormDirty: setDirtyForm,
      }}
    >
      {children}
    </EditFormTemplateContext.Provider>
  );
};

export function useEditFormTemplateContext(): ContextValue {
  const context = useContext(EditFormTemplateContext);

  if (context === undefined) {
    throw new Error(
      "useEditFormTemplateContext must be used within a FormTemplateEditProvider"
    );
  }

  return context;
}

type CalculateArgs = {
  /**
   * Force to calculate diff, ignoring context.isPublishedMode
   * @default false
   */
  force?: boolean;
};
export const useCalculateDiff = () => {
  const context = useEditFormTemplateContext();
  const unsafeCalculateDiff = React.useCallback(
    async (props: CalculateArgs = {}) => {
      const { force = false } = props;
      if (!force && !context.isPublishedMode) {
        // still in draft mode, we don't need to calculate diff
        return;
      }

      const response = await diffFormSchema({
        path: {
          formTemplateId: context.formTemplateId,
        },
      });

      const diff = response.data?.data;
      if (diff) {
        context.dispatch({ type: "SetDiff", diff });
      }

      return diff;
    },
    [context]
  );

  return { unsafeCalculateDiff };
};

const Loading = () => {
  return (
    <Flex gap="6" direction="column" width="100%">
      <Flex gap="2" direction="column">
        <Skeleton height="3rem" width="20rem" />
        <Skeleton height="2rem" width="10rem" alignSelf="flex-end" />
        <Skeleton height="10rem" />
      </Flex>
      <Flex gap="2" direction="column">
        <Skeleton height="3rem" width="20rem" />
        <Skeleton height="2rem" width="10rem" alignSelf="flex-end" />
        <Skeleton height="10rem" />
      </Flex>
    </Flex>
  );
};
